import React, { useCallback, useMemo, useState } from "react";
import { Eye, Gift, Copy, ChevronUp, ChevronDown, Users, SearchX, Tag, ImageDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatCurrency } from "@/lib/commission";
import { InfluencerTierBadge } from "../tiers/InfluencerTierBadge";
import { InfluencerFilters } from "./InfluencerFilters";
import {
  EMPTY_FILTERS,
  copyCoupon,
  hasActiveFilters,
  importInstagramAvatar,
  type InfluencerFilterState,
} from "./utils";
import {
  currentMonthKey,
  isActive,
  type InfluencerRecord,
} from "./hooks/useInfluencersData";

/** Sales at or above the Elite requirement get visual emphasis. */
const HIGH_SALES_THRESHOLD = 800;

type SortKey = "name" | "sales" | "commission";
type SortDirection = "asc" | "desc";

function initials(name: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

interface RowProps {
  record: InfluencerRecord;
  index: number;
  monthNow: string;
  importingAvatar: boolean;
  onOpenDetail: (record: InfluencerRecord) => void;
  onOpenTierSelector: (record: InfluencerRecord) => void;
  onManageCoupon: (record: InfluencerRecord) => void;
  onImportAvatar: (record: InfluencerRecord) => void;
}

const GridRow = React.memo<RowProps>(
  ({ record: r, index, monthNow, importingAvatar, onOpenDetail, onOpenTierSelector, onManageCoupon, onImportAvatar }) => {
    const month = r.months[monthNow];
    const sales = month?.total_sales || 0;
    const active = isActive(r);

    return (
      <tr
        className={`group border-b border-white/5 transition-colors hover:bg-white/[0.06] ${
          index % 2 === 1 ? "bg-white/[0.015]" : ""
        }`}
      >
        {/* Influencer */}
        <td className="px-4 py-2.5 max-w-[240px]">
          <button
            onClick={() => onOpenDetail(r)}
            className="flex items-center gap-2.5 text-left w-full"
            title="Ver detalhes"
          >
            <Avatar className="h-8 w-8 border border-white/10 shrink-0">
              {r.avatar_url && <AvatarImage src={r.avatar_url} alt="" />}
              <AvatarFallback className="bg-white/10 text-[11px] font-semibold">
                {initials(r.name)}
              </AvatarFallback>
            </Avatar>
            <span className="min-w-0">
              <span className="block truncate font-medium group-hover:text-primary transition-colors">
                {r.name || "—"}
              </span>
              {r.social_media && (
                <span className="block truncate text-xs text-muted-foreground">{r.social_media}</span>
              )}
            </span>
          </button>
        </td>

        {/* Coupon + copy */}
        <td className="px-3 py-2.5 whitespace-nowrap">
          <span className="inline-flex items-center gap-1.5">
            <Tag className="h-3 w-3 text-primary" />
            <span className="font-bold tracking-wider uppercase text-xs">{r.coupon || "—"}</span>
            {r.coupon && (
              <button
                onClick={() => copyCoupon(r.coupon)}
                title="Copiar cupom"
                className="text-muted-foreground/40 hover:text-primary opacity-0 group-hover:opacity-100 transition-all"
              >
                <Copy className="h-3 w-3" />
              </button>
            )}
          </span>
        </td>

        {/* Tier */}
        <td className="px-3 py-2.5">
          <button
            onClick={() => onOpenTierSelector(r)}
            title="Alterar categoria"
            className="transition-transform hover:scale-[1.03] active:scale-95"
          >
            <InfluencerTierBadge tierId={r.tier_id} />
          </button>
        </td>

        {/* Sales this month */}
        <td
          className={`text-right px-4 py-2.5 tabular-nums ${
            sales === 0
              ? "text-muted-foreground/30"
              : sales >= HIGH_SALES_THRESHOLD
                ? "font-semibold text-primary"
                : ""
          }`}
        >
          {formatCurrency(sales)}
        </td>

        {/* Commission: value + rate stacked */}
        <td className="text-right px-4 py-2.5 whitespace-nowrap">
          <span className={`block tabular-nums font-medium ${!month?.commission_amount ? "text-muted-foreground/30" : ""}`}>
            {formatCurrency(month?.commission_amount || 0)}
          </span>
          <span className="block text-[11px] text-muted-foreground tabular-nums">
            {month?.commission_rate ? `${Math.round(month.commission_rate * 100)}%` : "—"}
          </span>
        </td>

        {/* Status */}
        <td className="text-center px-3 py-2.5 whitespace-nowrap">
          <span className="inline-flex items-center gap-1.5 text-xs">
            <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-green-400" : "bg-white/20"}`} />
            <span className={active ? "text-green-400" : "text-muted-foreground"}>
              {active ? "Ativa" : "Inativa"}
            </span>
          </span>
        </td>

        {/* Actions */}
        <td className="px-3 py-2.5">
          <div className="flex items-center justify-center gap-1">
            <button
              onClick={() => onOpenDetail(r)}
              title="Ver detalhes e histórico financeiro"
              className="rounded-md p-1.5 text-muted-foreground hover:text-primary hover:bg-white/10 transition-colors"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={() => onManageCoupon(r)}
              title="Gerenciar cupom"
              className="rounded-md p-1.5 text-muted-foreground hover:text-primary hover:bg-white/10 transition-colors"
            >
              <Gift className="h-4 w-4" />
            </button>
            {r.social_media && (
              <button
                onClick={() => onImportAvatar(r)}
                disabled={importingAvatar}
                title={r.avatar_url ? "Atualizar avatar do Instagram" : "Importar avatar do Instagram"}
                className="rounded-md p-1.5 text-muted-foreground hover:text-primary hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                {importingAvatar ? (
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                ) : (
                  <ImageDown className="h-4 w-4" />
                )}
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  }
);
GridRow.displayName = "GridRow";

interface InfluencerDataGridProps {
  rows: InfluencerRecord[];
  loading: boolean;
  onOpenDetail: (record: InfluencerRecord) => void;
  onOpenTierSelector: (record: InfluencerRecord) => void;
  onManageCoupon: (record: InfluencerRecord) => void;
  onAvatarImported: (userId: string, avatarUrl: string) => void;
}

export const InfluencerDataGrid: React.FC<InfluencerDataGridProps> = ({
  rows,
  loading,
  onOpenDetail,
  onOpenTierSelector,
  onManageCoupon,
  onAvatarImported,
}) => {
  const [filters, setFilters] = useState<InfluencerFilterState>(EMPTY_FILTERS);
  const [sortKey, setSortKey] = useState<SortKey>("sales");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [importingAvatarId, setImportingAvatarId] = useState<string | null>(null);

  const handleImportAvatar = useCallback(
    async (record: InfluencerRecord) => {
      if (importingAvatarId) return; // one request at a time — no batch fetching
      setImportingAvatarId(record.id);
      try {
        const avatarUrl = await importInstagramAvatar(record.id, !!record.avatar_url);
        onAvatarImported(record.id, avatarUrl);
        toast.success("Avatar atualizado!");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Não foi possível importar");
      } finally {
        setImportingAvatarId(null);
      }
    },
    [importingAvatarId, onAvatarImported]
  );

  const monthNow = currentMonthKey();

  const filtered = useMemo(() => {
    const q = filters.search.toLowerCase().trim();
    const min = filters.minSales === "" ? null : Number(filters.minSales);
    const max = filters.maxSales === "" ? null : Number(filters.maxSales);
    return rows.filter((r) => {
      if (q && !(r.name || "").toLowerCase().includes(q) && !(r.coupon || "").toLowerCase().includes(q)) {
        return false;
      }
      if (filters.tiers.length > 0) {
        const tierValue = r.tier_id ?? "none";
        if (!filters.tiers.includes(tierValue as (typeof filters.tiers)[number])) return false;
      }
      if (filters.status === "active" && !isActive(r)) return false;
      if (filters.status === "inactive" && isActive(r)) return false;
      const sales = r.months[monthNow]?.total_sales || 0;
      if (min !== null && !Number.isNaN(min) && sales < min) return false;
      if (max !== null && !Number.isNaN(max) && sales > max) return false;
      return true;
    });
  }, [rows, filters, monthNow]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      if (sortKey === "name") {
        aVal = (a.name || "").toLowerCase();
        bVal = (b.name || "").toLowerCase();
      } else if (sortKey === "commission") {
        aVal = a.months[monthNow]?.commission_amount || 0;
        bVal = b.months[monthNow]?.commission_amount || 0;
      } else {
        aVal = a.months[monthNow]?.total_sales || 0;
        bVal = b.months[monthNow]?.total_sales || 0;
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [filtered, sortKey, sortDir, monthNow]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const SortIcon: React.FC<{ columnKey: SortKey }> = ({ columnKey }) => {
    if (sortKey !== columnKey) {
      return <ChevronDown className="w-3 h-3 ml-1 opacity-0 group-hover/th:opacity-40 transition-opacity" />;
    }
    return sortDir === "asc" ? (
      <ChevronUp className="w-3 h-3 ml-1 text-primary" />
    ) : (
      <ChevronDown className="w-3 h-3 ml-1 text-primary" />
    );
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
        <div className="flex gap-2">
          <div className="skeleton-shimmer h-9 w-60" />
          <div className="skeleton-shimmer h-9 w-28" />
          <div className="skeleton-shimmer h-9 w-28" />
        </div>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="skeleton-shimmer h-8 w-8 rounded-full" />
            <div className="skeleton-shimmer h-4 flex-1" />
            <div className="skeleton-shimmer h-4 w-24" />
            <div className="skeleton-shimmer h-4 w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/[0.02] py-16 text-center space-y-2">
        <Users className="h-8 w-8 mx-auto text-muted-foreground/40" />
        <p className="font-medium">Nenhuma influenciadora registrada</p>
        <p className="text-sm text-muted-foreground">
          As influenciadoras aparecem aqui assim que se registram na plataforma.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <InfluencerFilters
        value={filters}
        onChange={setFilters}
        resultCount={sorted.length}
        totalCount={rows.length}
      />

      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {sorted.length === 0 ? (
          <div className="py-16 text-center space-y-3">
            <SearchX className="h-8 w-8 mx-auto text-muted-foreground/40" />
            <div>
              <p className="font-medium">Nenhum resultado para estes filtros</p>
              <p className="text-sm text-muted-foreground">Tente ajustar a busca ou os filtros ativos.</p>
            </div>
            {hasActiveFilters(filters) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="border-white/10 bg-white/5 hover:bg-white/10"
              >
                Limpar filtros
              </Button>
            )}
          </div>
        ) : (
          <div className="max-h-[65vh] overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-background/95 backdrop-blur border-b border-white/10 text-xs uppercase tracking-wide text-muted-foreground">
                  <th
                    onClick={() => handleSort("name")}
                    className="group/th text-left px-4 py-3 font-semibold cursor-pointer hover:text-foreground select-none whitespace-nowrap"
                  >
                    <span className="inline-flex items-center">Influenciadora<SortIcon columnKey="name" /></span>
                  </th>
                  <th className="text-left px-3 py-3 font-semibold whitespace-nowrap">Cupom</th>
                  <th className="text-left px-3 py-3 font-semibold whitespace-nowrap">Categoria</th>
                  <th
                    onClick={() => handleSort("sales")}
                    className="group/th text-right px-4 py-3 font-semibold cursor-pointer hover:text-foreground select-none whitespace-nowrap"
                  >
                    <span className="inline-flex items-center justify-end">Vendas (mês)<SortIcon columnKey="sales" /></span>
                  </th>
                  <th
                    onClick={() => handleSort("commission")}
                    className="group/th text-right px-4 py-3 font-semibold cursor-pointer hover:text-foreground select-none whitespace-nowrap"
                  >
                    <span className="inline-flex items-center justify-end">Comissão (mês)<SortIcon columnKey="commission" /></span>
                  </th>
                  <th className="text-center px-3 py-3 font-semibold whitespace-nowrap">Status</th>
                  <th className="text-center px-3 py-3 font-semibold whitespace-nowrap">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, idx) => (
                  <GridRow
                    key={r.id}
                    record={r}
                    index={idx}
                    monthNow={monthNow}
                    importingAvatar={importingAvatarId === r.id}
                    onOpenDetail={onOpenDetail}
                    onOpenTierSelector={onOpenTierSelector}
                    onManageCoupon={onManageCoupon}
                    onImportAvatar={handleImportAvatar}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InfluencerDataGrid;
